import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/contexts/AuthContext";
import {
  useProjectsListQuery,
  useUserProjectStatsQuery,
  useDeleteProject,
} from "@/features/Projects";
import type { ProjectStatus } from "@/features/Projects";
import { LoadingSpinner } from "@/components";

import {
  ProjectsHeader,
  ProjectsStats,
  ProjectsFilters,
  ProjectsGrid,
  EmptyState,
  DeleteProjectModal,
} from "../../components";

export default function ProjectsList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<ProjectStatus | "all">("all");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  // Queries
  const { data: projects = [], isLoading } = useProjectsListQuery(
    user?.uid || ""
  );

  const { data: stats, isLoading: statsLoading } = useUserProjectStatsQuery(
    user?.uid || ""
  );

  // Delete functionality
  const {
    deleteModal,
    isDeleting,
    openDeleteModal,
    closeDeleteModal,
    confirmDelete,
  } = useDeleteProject();

  // Filter projects
  const filteredProjects = projects.filter((project) => {
    const matchesSearch =
      project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (project.description || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || project.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Handlers
  const handleCreateProject = () => {
    navigate("/projects/create");
  };

  const handleDeleteProject = (projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    if (project) {
      openDeleteModal(projectId, project.name);
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-8">
      <ProjectsHeader onCreateProject={handleCreateProject} />

      {stats && (
        <ProjectsStats stats={stats} isLoading={statsLoading} />
      )}

      <ProjectsFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      {filteredProjects.length === 0 ? (
        <EmptyState
          hasProjects={projects.length > 0}
          onCreateProject={handleCreateProject}
        />
      ) : (
        <ProjectsGrid
          projects={filteredProjects}
          viewMode={viewMode}
          onDeleteProject={handleDeleteProject}
          isDeleting={isDeleting}
        />
      )}

      <DeleteProjectModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        projectName={deleteModal.projectName}
        isDeleting={isDeleting}
      />
    </div>
  );
}